
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Footer from "@/components/sections/Footer";
import { sanitizeHtml } from "@/lib/sanitize";

const ArticleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  console.log("[ArticleDetail] Rendering with id:", id);

  const { data: article, isLoading } = useQuery({
    queryKey: ["article", id],
    queryFn: async () => {
      console.log("[ArticleDetail] Fetching article data for id:", id);
      const { data, error } = await supabase
        .from("articles")
        .select("*")
        .eq("id", id)
        .eq("status", "published")
        .single();

      if (error) {
        console.error("[ArticleDetail] Error fetching article:", error);
        if (error.code === 'PGRST116') {
          // Article not found or not published
          navigate('/');
        }
        throw error;
      }

      console.log("[ArticleDetail] Article data fetched:", data);
      return data;
    },
  });

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8 mt-24">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-3/4 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
          <div className="h-64 bg-gray-200 rounded mb-8"></div>
          <div className="space-y-4">
            <div className="h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            <div className="h-4 bg-gray-200 rounded w-4/6"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="container mx-auto px-4 py-8 mt-24">
        <h1 className="text-2xl font-bold mb-4">Article not found</h1>
        <Link to="/">
          <Button variant="outline">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Home
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-grow bg-gray-50">
        <div className="container mx-auto px-4 py-8 mt-24">
          <Link to="/">
            <Button variant="outline" className="mb-6 animate-fade-up">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Button>
          </Link>
          
          <article className="max-w-4xl mx-auto bg-white p-8 rounded-lg shadow-sm">
            <h1 className="text-4xl font-bold mb-4 animate-fade-up">{article.title}</h1>
            <p className="text-gray-600 mb-8 animate-fade-up" style={{ animationDelay: "0.1s" }}>{article.description}</p>
            
            <img
              src={article.image_url}
              alt={article.title}
              className="w-full h-[400px] object-cover rounded-lg mb-8 animate-fade-up"
              style={{ animationDelay: "0.2s" }}
            />
            
            <div 
              className="prose max-w-none animate-fade-up"
              style={{ animationDelay: "0.3s" }}
              dangerouslySetInnerHTML={{ __html: sanitizeHtml(article.content) }}
            />
          </article>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ArticleDetail;
